// src/pages/demo/UAEDemoPage.js
import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Backdrop,
  Fade,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PlayArrow, 
  Pause, 
  VolumeUp,
  Language,
  CheckCircle,
  Business,
  Public,
  Close,
  ArrowBack,
  Translate,
  RecordVoiceOver,
  Psychology,
  TrendingUp,
  Architecture,
  School
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../utils/color';
import useSEO from '../../hooks/useSEO';

// Styled Components with UAE theme colors (red, green, white, black)
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #C8102E 0%, #8B0000 50%, #00732F 100%)`, // UAE flag colors gradient
  padding: theme.spacing(6, 0, 2, 0),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
}));

const VideoCard = styled(Card)(({ theme, isOriginal }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: isOriginal 
    ? `2px solid #C8102E` 
    : `1px solid ${COLORS.border.primary}`,
  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px rgba(200,16,46,0.3)`,
  },
}));

const VideoContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  paddingTop: '56.25%', // 16:9 aspect ratio
  backgroundColor: '#000',
  borderRadius: '8px',
  overflow: 'hidden',
});

const VideoPlaceholder = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
}));

const VideoModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '90vw',
  maxWidth: '1200px',
  backgroundColor: COLORS.background.primary,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  outline: 'none',
  border: `2px solid #C8102E`,
  boxShadow: `0 20px 40px rgba(200,16,46,0.4)`,
}));

const ModalVideo = styled('video')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '70vh',
  borderRadius: theme.spacing(1),
  backgroundColor: '#000',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: `${COLORS.background.secondary}90`,
  color: COLORS.text.primary,
  zIndex: 1,
  '&:hover': {
    backgroundColor: COLORS.background.secondary,
  },
}));

const LanguageFlag = styled(Box)({
  fontSize: '2rem',
  marginBottom: '8px',
});

const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: `rgba(200,16,46,0.8)`,
  color: 'white',
  width: 60,
  height: 60,
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#C8102E',
    transform: 'scale(1.1)',
  },
}));

const ContextCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, rgba(200,16,46,0.05) 0%, rgba(0,115,47,0.05) 100%)`,
  border: `1px solid rgba(200,16,46,0.2)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const UAEDemoPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  useSEO({
    title: 'Sheikh MBZ Climate Speech AI Dubbing Demo | DubLabs',
    description: 'Experience DubLabs AI video dubbing with HH Sheikh Mohamed Bin Zayed remarks at the Major Economies Forum — Arabic speech into multiple world languages.',
    keywords: 'Sheikh Mohamed Bin Zayed speech, Major Economies Forum, climate speech translation, Arabic to English dubbing, UAE climate leadership'
  });

  // Demo videos for Sheikh Mohammed speeches
  const demoVideos = [
    {
      id: 'original',
      title: 'Original Speech',
      language: 'العربية (Arabic)',
      flag: '🇦🇪',
      speakers: 'Original',
      description: 'Major Economies Forum on Energy & Climate',
      isOriginal: true,
      videoUrl: '/demo/uae/original-arabic.mp4', // You'll need to add these videos
      features: ['Original Arabic Voice', 'Authoritative Delivery', 'Climate Leadership'],
      context: 'MEF Climate Summit'
    },
    {
      id: 'english',
      title: 'English Version',
      language: 'English',
      flag: '🇬🇧',
      speakers: '1.5B+ speakers',
      description: 'AI-dubbed from original Arabic',
      videoUrl: '/demo/uae/english.mp4',
      features: ['Diplomatic Tone', 'Clear Translation', 'Perfect Lip-Sync'],
      context: 'For global audiences'
    },
    {
      id: 'hindi',
      title: 'Hindi Version',
      language: 'हिंदी',
      flag: '🇮🇳',
      speakers: '600M+ speakers',
      description: 'AI-dubbed from Arabic for South Asian audiences',
      videoUrl: '/demo/uae/hindi.mp4',
      features: ['Regional Appeal', 'Business Context', 'Clear Pronunciation'],
      context: 'For South Asian partnerships'
    },
    {
      id: 'mandarin',
      title: 'Mandarin Version',
      language: '中文',
      flag: '🇨🇳', 
      speakers: '900M+ speakers',
      description: 'AI-dubbed from Arabic for Chinese partners',
      videoUrl: '/demo/uae/mandarin.mp4',
      features: ['Business Tone', 'Investment Focus', 'Cultural Bridge'],
      context: 'For Belt & Road initiatives'
    }
  ];

  const useCases = [
    {
      icon: <Architecture sx={{ fontSize: 30, color: '#C8102E' }} />,
      title: 'Climate Summits & COP',
      description: 'Share UAE\'s climate commitments with global stakeholders'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 30, color: '#00732F' }} />,
      title: 'Energy Transition Forums',
      description: 'Communicate energy strategies to international partners'
    },
    {
      icon: <School sx={{ fontSize: 30, color: '#C8102E' }} />,
      title: 'Sustainability Education',
      description: 'Share UAE\'s sustainable development goals globally'
    },
    {
      icon: <Public sx={{ fontSize: 30, color: '#00732F' }} />,
      title: 'Global Diplomacy',
      description: 'Enhance diplomatic communications across all languages'
    }
  ];

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVideo(null);
  };

  const handleGetStarted = () => {
    navigate('/contact');
  };

  const handleBackToDemo = () => {
    navigate('/demo');
  };

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToDemo}
            sx={{
              color: 'white',
              mb: 3,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            Back to Main Demo
          </Button>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: 'white',
                mb: 2,
              }}
            >
              🇦🇪 Sheikh Mohamed Bin Zayed
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.2rem', md: '1.8rem' }
              }}
            >
              Major Economies Forum on Energy & Climate
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                mb: 4,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Experience HH Sheikh Mohamed Bin Zayed's powerful remarks from the Major Economies Forum 
              on Energy and Climate. Originally delivered in Arabic, witness how AI dubbing perfectly 
              translates his vision for sustainable development to global audiences.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
              <Chip 
                icon={<RecordVoiceOver />}
                label="Authentic Voice" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
              <Chip 
                icon={<Psychology />}
                label="Vision Preserved" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
              <Chip 
                icon={<Language />}
                label="Global Reach" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
            </Box>
          </Box>
        </Container>
      </HeroSection>

      {/* Context Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ContextCard>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#C8102E', mb: 2 }}>
            UAE's Climate Leadership on the Global Stage
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.text.secondary, lineHeight: 1.8 }}>
            This demonstration showcases how DubLabs' AI technology transforms HH Sheikh Mohamed Bin Zayed's 
            Arabic remarks at the Major Economies Forum on Energy and Climate into multiple languages. 
            Perfect for international climate conferences, diplomatic communications, and global sustainability 
            initiatives. Our technology preserves the authority and commitment in his message about the UAE's 
            role in addressing climate change and energy transition.
          </Typography>
        </ContextCard>
      </Container>

      {/* Video Demo Section */}
      <Container maxWidth="xl" sx={{ py: 1 }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: COLORS.text.primary,
            mb: 2,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}
        >
          From Arabic to the World: Climate Leadership Message
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: COLORS.text.secondary,
            mb: 6,
            fontSize: '1.1rem',
            maxWidth: 800,
            mx: 'auto'
          }}
        >
          Originally delivered in Arabic, Sheikh Mohamed Bin Zayed's climate forum remarks reach 
          global audiences through perfect AI-powered multilingual dubbing, maintaining the gravitas 
          and urgency of his message.
        </Typography>

        {/* Video Grid */}
        <Grid container spacing={3}>
          {demoVideos.map((video, index) => (
            <Grid item xs={12} md={6} lg={4} key={video.id}>
              <VideoCard isOriginal={video.isOriginal}>
                <VideoContainer>
                  <VideoPlaceholder onClick={() => handleVideoClick(video)}>
                    <LanguageFlag>{video.flag}</LanguageFlag>
                    <PlayButton>
                      <PlayArrow sx={{ fontSize: 30 }} />
                    </PlayButton>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {video.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Click to play
                    </Typography>
                  </VideoPlaceholder>
                </VideoContainer>
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text.primary }}>
                      {video.language}
                    </Typography>
                    <Chip 
                      label={video.speakers} 
                      size="small"
                      sx={{ backgroundColor: `rgba(200,16,46,0.1)`, color: '#C8102E' }} 
                    />
                  </Box>
                  
                  <Typography variant="body2" sx={{ color: COLORS.text.secondary, mb: 2 }}>
                    {video.description}
                  </Typography>
                  
                  <Typography variant="caption" sx={{ color: '#C8102E', fontWeight: 600, display: 'block', mb: 2 }}>
                    Context: {video.context}
                  </Typography>
                  
                  <List dense sx={{ p: 0 }}>
                    {video.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#C8102E' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            sx: { color: COLORS.text.tertiary, fontSize: '0.9rem' } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </VideoCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Use Cases Section */}
      <Box sx={{ backgroundColor: COLORS.background.secondary, py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: COLORS.text.primary,
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Perfect for UAE's Global Initiatives
          </Typography>
          
          <Grid container spacing={4}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
                  border: `1px solid rgba(200,16,46,0.2)`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px rgba(200,16,46,0.2)`,
                  }
                }}>
                  <CardContent sx={{ p: 4, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {useCase.icon}
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600, color: COLORS.text.primary }}>
                        {useCase.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: COLORS.text.secondary, lineHeight: 1.6 }}>
                      {useCase.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#C8102E', mb: 1 }}>
                200+
              </Typography>
              <Typography variant="body1" sx={{ color: COLORS.text.secondary }}>
                Nationalities in UAE
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#00732F', mb: 1 }}>
                30+
              </Typography>
              <Typography variant="body1" sx={{ color: COLORS.text.secondary }}>
                Languages Supported
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#C8102E', mb: 1 }}>
                195+
              </Typography>
              <Typography variant="body1" sx={{ color: COLORS.text.secondary }}>
                Countries in Climate Forums
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Copyright Disclaimer */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, rgba(200,16,46,0.05) 0%, rgba(0,115,47,0.05) 100%)`,
          border: `1px solid rgba(200,16,46,0.2)`,
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontStyle: 'italic' }}>
            Demo purposes only. All video content is for demonstration and does not represent actual speeches. 
            DubLabs respects all copyright and intellectual property rights.
          </Typography>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Box sx={{ 
          background: `linear-gradient(135deg, #C8102E, #00732F)`, 
          borderRadius: 3, 
          p: 6,
          color: 'white'
        }}>
          <Architecture sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Amplify UAE's Climate Leadership
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Perfect for climate conferences, diplomatic forums, and international sustainability initiatives
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              backgroundColor: 'white',
              color: '#C8102E',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            Get in Touch
          </Button>
        </Box>
      </Container>

      {/* Large Video Modal */}
      <VideoModal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
        }}
      >
        <Fade in={modalOpen}>
          <ModalContent>
            <CloseButton onClick={handleCloseModal}>
              <Close />
            </CloseButton>
            
            {selectedVideo && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ fontSize: '2rem' }}>{selectedVideo.flag}</Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: COLORS.text.primary }}>
                      {selectedVideo.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                      {selectedVideo.description} • {selectedVideo.context}
                    </Typography>
                  </Box>
                </Box>
                
                <ModalVideo 
                  controls
                  autoPlay
                  loop
                  preload="metadata"
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  <Typography sx={{ p: 4, textAlign: 'center', color: COLORS.text.secondary }}>
                    Video placeholder - Add Sheikh Mohamed Bin Zayed climate forum videos to /public/demo/uae/
                  </Typography>
                </ModalVideo>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text.primary, mb: 1 }}>
                    Key Features:
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedVideo.features.map((feature, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#C8102E', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                            {feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </ModalContent>
        </Fade>
      </VideoModal>
    </Box>
  );
};

export default UAEDemoPage;